import React from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useStateSelector } from 'src/redux';

const Wrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  position: relative;

  .rt-icon {
    flex-shrink: 0;
  }

  .rt-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
  }

  .rt-title {
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.prussianBlue};
    line-height: 1.3;
  }

  .rt-subtitle {
    font-family: Poppins, sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    line-height: 1.4;
  }
`;

const CtaBtn = styled.button`
  margin-top: 8px;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme }) => theme.colors.white};
  font-family: Poppins, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.cultured};
  }

  svg {
    flex-shrink: 0;
  }
`;

const ArrowRight = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 12h14M13 5l7 7-7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Icons ---
const BuildingIcon: React.FC = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="19" cy="19" r="19" fill={colors.statusOrange} />
      <g transform="translate(7 7)">
        <path
          d="M8 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H11.697M18 14V18H22M18 11V7C18 6.46957 17.7893 5.96086 17.4142 5.58579C17.0391 5.21071 16.5304 5 16 5H14"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 11H12M8 15H11M8 5C8 4.46957 8.21071 3.96086 8.58579 3.58579C8.96086 3.21071 9.46957 3 10 3H12C12.5304 3 13.0391 3.21071 13.4142 3.58579C13.7893 3.96086 14 4.46957 14 5C14 5.53043 13.7893 6.03914 13.4142 6.41421C13.0391 6.78929 12.5304 7 12 7H10C9.46957 7 8.96086 6.78929 8.58579 6.41421C8.21071 6.03914 8 5.53043 8 5ZM14 18C14 19.0609 14.4214 20.0783 15.1716 20.8284C15.9217 21.5786 16.9391 22 18 22C19.0609 22 20.0783 21.5786 20.8284 20.8284C21.5786 20.0783 22 19.0609 22 18C22 16.9391 21.5786 15.9217 20.8284 15.1716C20.0783 14.4214 19.0609 14 18 14C16.9391 14 15.9217 14.4214 15.1716 15.1716C14.4214 15.9217 14 16.9391 14 18Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

const FailedIcon: React.FC = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="19" cy="19" r="19" fill={colors.error} />
      <g transform="translate(7 7)">
        <path
          d="M8 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H11.697M18 11V7C18 6.46957 17.7893 5.96086 17.4142 5.58579C17.0391 5.21071 16.5304 5 16 5H14"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21.5 13.5L15 20M15 13.5L21.5 20"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 11H12M8 15H11M8 5C8 4.46957 8.21071 3.96086 8.58579 3.58579C8.96086 3.21071 9.46957 3 10 3H12C12.5304 3 13.0391 3.21071 13.4142 3.58579C13.7893 3.96086 14 4.46957 14 5C14 5.53043 13.7893 6.03914 13.4142 6.41421C13.0391 6.78929 12.5304 7 12 7H10C9.46957 7 8.96086 6.78929 8.58579 6.41421C8.21071 6.03914 8 5.53043 8 5Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

const SuccessIcon: React.FC = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="19" cy="19" r="19" fill={colors.statusGreen} />
      <g transform="translate(7 7)">
        <path
          d="M8 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H11.697M18 11V7C18 6.46957 17.7893 5.96086 17.4142 5.58579C17.0391 5.21071 16.5304 5 16 5H14"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.087 18.9651L14.0159 16.7575C13.9043 16.6386 13.7529 16.5717 13.5951 16.5717C13.4373 16.5717 13.2859 16.6386 13.1743 16.7575C13.0627 16.8765 13 17.0378 13 17.206C13 17.2893 13.0154 17.3718 13.0453 17.4488C13.0752 17.5257 13.119 17.5957 13.1743 17.6546L15.6692 20.3139C15.902 20.562 16.278 20.562 16.5108 20.3139L22.8257 13.5828C22.9373 13.4639 23 13.3025 23 13.1343C23 12.9661 22.9373 12.8047 22.8257 12.6858C22.7141 12.5668 22.5627 12.5 22.4049 12.5C22.2471 12.5 22.0957 12.5668 21.9841 12.6858L16.087 18.9651Z"
          fill="white"
        />
        <path
          d="M8 11H12M8 15H11M8 5C8 4.46957 8.21071 3.96086 8.58579 3.58579C8.96086 3.21071 9.46957 3 10 3H12C12.5304 3 13.0391 3.21071 13.4142 3.58579C13.7893 3.96086 14 4.46957 14 5C14 5.53043 13.7893 6.03914 13.4142 6.41421C13.0391 6.78929 12.5304 7 12 7H10C9.46957 7 8.96086 6.78929 8.58579 6.41421C8.21071 6.03914 8 5.53043 8 5Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

// --- Building toast ---
interface ReportBuildingToastProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  // injected by react-toastify when this component is rendered as a toast
  closeToast?: () => void;
}

const ReportBuildingContent: React.FC<ReportBuildingToastProps> = ({
  title = "We're building your report.",
  subtitle = '',
  // subtitle = "Keep working; we'll notify you when it's ready.",
  ctaLabel = 'View progress status',
  onCtaClick,
  closeToast,
}) => (
  <Wrap>
    <div className="rt-icon">
      <BuildingIcon />
    </div>
    <div className="rt-content">
      <span className="rt-title">{title}</span>
      {subtitle && <span className="rt-subtitle">{subtitle}</span>}
      {onCtaClick && (
        <CtaBtn
          type="button"
          onClick={() => {
            onCtaClick();
            closeToast?.();
          }}
        >
          {ctaLabel}
          <ArrowRight />
        </CtaBtn>
      )}
    </div>
  </Wrap>
);

export const showReportBuildingToast = (overrides?: ReportBuildingToastProps) => {
  toast(<ReportBuildingContent {...overrides} />, {
    type: 'warning',
    icon: false,
    autoClose: 8000,
  });
};

// --- Failed toast ---
interface ReportFailedToastProps {
  title?: string;
  subtitle?: string;
}

const ReportFailedContent: React.FC<ReportFailedToastProps> = ({
  title = 'Report generation failed',
  subtitle = 'Description for the error can be added here',
}) => (
  <Wrap>
    <div className="rt-icon">
      <FailedIcon />
    </div>
    <div className="rt-content">
      <span className="rt-title">{title}</span>
      {subtitle && <span className="rt-subtitle">{subtitle}</span>}
    </div>
  </Wrap>
);

export const showReportFailedToast = (overrides?: ReportFailedToastProps) => {
  toast(<ReportFailedContent {...overrides} />, {
    type: 'error',
    icon: false,
    autoClose: 8000,
  });
};

// --- Success toast ---
interface ReportSuccessToastProps {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  // injected by react-toastify when this component is rendered as a toast
  closeToast?: () => void;
}

const ReportSuccessContent: React.FC<ReportSuccessToastProps> = ({
  title = 'Report generated successfully',
  subtitle = 'Download report at the report centre',
  ctaLabel = 'View report',
  onCtaClick,
  closeToast,
}) => (
  <Wrap>
    <div className="rt-icon">
      <SuccessIcon />
    </div>
    <div className="rt-content">
      <span className="rt-title">{title}</span>
      <span className="rt-subtitle">{subtitle}</span>
      {onCtaClick && (
        <CtaBtn
          type="button"
          onClick={() => {
            onCtaClick();
            closeToast?.();
          }}
        >
          {ctaLabel}
          <ArrowRight />
        </CtaBtn>
      )}
    </div>
  </Wrap>
);

export const showReportSuccessToast = (overrides?: ReportSuccessToastProps) => {
  toast(<ReportSuccessContent {...overrides} />, {
    type: 'success',
    icon: false,
    autoClose: 8000,
  });
};
